import { cn } from '@staff-app/ui';

/**
 * Colour token → Tailwind class. THE single source for OptiClock project colours.
 *
 * This map is why `color` is a CLOSED token set on the api rather than a free string: a
 * user-supplied value can never reach a `style` attribute, and Tailwind only ships classes it
 * can find literally in source — a runtime-built class name would be purged.
 *
 * It lives here rather than beside any one component because four places render the same dot
 * (the picker, the entry rows, the category admin, the stacked bar). Three of them used to hold
 * their own copy, which is exactly how a renamed token ends up coloured in one view and grey in
 * another.
 *
 * Keep in step with OPTICLOCK_COLORS (@staff-app/shared-types).
 */
export const PROJECT_COLOR_CLASS: Record<string, string> = {
  slate: 'bg-slate-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  green: 'bg-green-500',
  teal: 'bg-teal-500',
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
  pink: 'bg-pink-500',
};

/** An unknown or missing token still renders SOMETHING — an invisible segment reads as a bug. */
export const PROJECT_COLOR_FALLBACK = 'bg-slate-400';

export function projectColorClass(color?: string | null): string {
  return PROJECT_COLOR_CLASS[color ?? ''] ?? PROJECT_COLOR_FALLBACK;
}

/** The little round swatch shown before a project name. */
export function ProjectDot({ color, className }: { color?: string | null; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', projectColorClass(color), className)}
    />
  );
}
